import { useEffect, useState } from 'react';
import {
  brand,
  footer,
  founders,
  icons,
  landing,
  nav,
  type FooterColumn,
  type FounderParagraph,
  type IconName,
  type NavItem,
} from '../content/site';
import { entegrasyonlarPage, type IntegrationCategory } from '../content/pages/entegrasyonlar';
import { asset } from '../lib/asset';
import { iletisimPage } from '../content/pages/iletisim';
import { sssPage, type FaqCategory, type FaqItem } from '../content/pages/sss';
import { legalByKey, legalCmsKey, legalCompany, legalDocs, legalHub, type LegalKey } from '../content/pages/yasal';
import { fetchCms } from '../lib/cms';
import { pricingPage } from '../content/pages/fiyatlar';
import type { Pricing } from '../lib/pricing';
import { formatDate } from '../lib/format';
import { staticLegalHtml } from '../lib/legalHtml';

/*
 * Admin panelinden yönetilen içerik. Her içerik önce statik hâliyle (src/content)
 * çizilir, CMS yanıt verince güncellenir; CMS'e ulaşılamazsa statik içerik kalır.
 * Böylece CMS kapalıyken de site eksiksiz açılır. (Site SPA olduğundan ilk HTML yine boştur;
 * SEO için içeriğin build sırasında sayfalara gömülmesi ayrıca ele alınacak.)
 *
 * Bir uç sayfa yüklemesi başına bir kez istenir; sonuç bellekte tutulur, sayfalar
 * arası geçişte tekrar istenmez ve statik hâl yeniden yanıp sönmez.
 *
 * Adres değişebilir (ör. aynı sayfa bileşeni bir yasal metinden diğerine geçer): dönen değer
 * her zaman o anki adrese aittir; yanıt gelmemişse o adresin statik yedeği döner.
 */
const requests = new Map<string, Promise<unknown>>();
const resolved = new Map<string, unknown>();
/** İsteği sonuçlanan (veri gelen ya da CMS'e ulaşılamayan) adresler. */
const settled = new Set<string>();

function load<T>(path: string): Promise<T | null> {
  let request = requests.get(path);
  if (!request) {
    request = fetchCms<T>(path).then((data) => {
      if (data !== null) resolved.set(path, data);
      settled.add(path);
      return data;
    });
    requests.set(path, request);
  }
  return request as Promise<T | null>;
}

interface CmsState<T> {
  data: T;
  /**
   * CMS yanıt verdi ya da ulaşılamadı; artık değer değişmeyecek. Statik yedekte olmayan bir kaydı
   * (admin'den yeni eklenmiş kurucu) arayan sayfa, buna bakmadan "bulunamadı" demez.
   */
  settled: boolean;
}

function useCmsState<TApi, T>(path: string, fallback: T, map: (data: TApi) => T): CmsState<T> {
  // Yalnız "yanıt geldi" bilgisini tutar; değer her çizimde önbellekten okunur.
  const [, setLoadedPath] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    load<TApi>(path).then(() => {
      if (active) setLoadedPath(path);
    });
    return () => {
      active = false;
    };
  }, [path]);

  return { data: resolved.has(path) ? map(resolved.get(path) as TApi) : fallback, settled: settled.has(path) };
}

function useCms<TApi, T>(path: string, fallback: T, map: (data: TApi) => T): T {
  return useCmsState(path, fallback, map).data;
}

/* ---------- Site ayarları ---------- */

interface SiteSettingsApi {
  companyLegalName: string;
  email: string;
  phone: string;
  phoneInternational: string;
  workingHours: string | null;
  addressTitle: string;
  addressDetail: string;
  companyShortName: string;
  taxOffice: string;
  taxNumber: string;
  mersisNumber: string;
  appUrl: string;
  tagline: string;
  instagramUrl: string | null;
  facebookUrl: string | null;
  linkedInUrl: string | null;
  xUrl: string | null;
  youTubeUrl: string | null;
}

export interface SiteInfo {
  email: string;
  phone: string;
  phoneHref: string;
  workingHours: string | null;
  address: string;
  addressDetail: string;
  appUrl: string;
  tagline: string;
  socials: { icon: IconName; href: string; label: string }[];
  copyright: string;
  /** Yasal Merkez'deki "Veri sorumlusu ve satıcı" kartı. */
  legal: { name: string; address: string; taxOffice: string; taxNumber: string; mersis: string; phone: string };
}

const staticSiteInfo: SiteInfo = {
  email: brand.email,
  phone: brand.phone,
  phoneHref: brand.phoneHref,
  workingHours: iletisimPage.cards.phone.note,
  address: brand.address,
  addressDetail: brand.addressDetail,
  appUrl: brand.appUrl,
  tagline: brand.tagline,
  socials: [...footer.socials],
  copyright: footer.copyright,
  legal: {
    name: legalCompany.name,
    address: legalCompany.address,
    taxOffice: legalCompany.taxOffice,
    taxNumber: legalCompany.taxNo,
    mersis: legalCompany.mersis,
    phone: legalCompany.phone,
  },
};

/** +905534815842 → +90 553 481 58 42 (yasal metinlerdeki yazım; backend'de de aynı biçim). */
const formatPhone = (international: string) =>
  international.replace(/^\+90(\d{3})(\d{3})(\d{2})(\d{2})$/, '+90 $1 $2 $3 $4');

// Footer'daki ikon sırası sabit; CMS'te boş bırakılan hesap gösterilmez.
const socialSlots = [
  { key: 'instagramUrl', icon: 'instagram', label: 'Instagram' },
  { key: 'facebookUrl', icon: 'facebook', label: 'Facebook' },
  { key: 'linkedInUrl', icon: 'linkedin', label: 'LinkedIn' },
  { key: 'xUrl', icon: 'x', label: 'X' },
  { key: 'youTubeUrl', icon: 'youtube', label: 'YouTube' },
] as const;

const mapSiteInfo = (s: SiteSettingsApi): SiteInfo => ({
  email: s.email,
  phone: s.phone,
  phoneHref: `tel:${s.phoneInternational}`,
  workingHours: s.workingHours,
  address: s.addressTitle,
  addressDetail: s.addressDetail,
  appUrl: s.appUrl,
  tagline: s.tagline,
  socials: socialSlots.filter((slot) => s[slot.key]).map((slot) => ({ icon: slot.icon, label: slot.label, href: s[slot.key] as string })),
  copyright: `© ${new Date().getFullYear()} ${s.companyShortName} Tüm hakları saklıdır.`,
  legal: {
    name: s.companyLegalName,
    address: s.addressDetail,
    taxOffice: s.taxOffice,
    taxNumber: s.taxNumber,
    mersis: s.mersisNumber,
    phone: formatPhone(s.phoneInternational),
  },
});

export function useSiteInfo(): SiteInfo {
  return useCms('/v1/public/site-settings', staticSiteInfo, mapSiteInfo);
}

/* ---------- SSS ---------- */

interface FaqCategoryApi {
  slug: string;
  title: string;
  items: { id: number; question: string; answer: string }[];
}

const mapFaq = (categories: FaqCategoryApi[]): FaqCategory[] =>
  categories.map((c) => ({
    id: c.slug,
    title: c.title,
    items: c.items.map((i) => ({ q: i.question, a: i.answer })),
  }));

export function useFaqCategories(): FaqCategory[] {
  return useCms('/v1/public/faq', sssPage.categories, mapFaq);
}

/* ---------- Fiyatlar sayfasının SSS'i ---------- */

// Aynı SSS kayıtları; admin'de "Fiyatlar sayfası" işaretli olanlar, o sayfanın sırasıyla.
const mapPricingFaq = (items: FaqCategoryApi['items']): FaqItem[] => items.map((i) => ({ q: i.question, a: i.answer }));

export function usePricingFaq(): readonly FaqItem[] {
  return useCms<FaqCategoryApi['items'], readonly FaqItem[]>('/v1/public/faq/pricing', pricingPage.faq.items, mapPricingFaq);
}

/* ---------- Yasal metinler ---------- */

interface LegalSummaryApi {
  key: string;
  title: string;
  summary: string;
  revisedOn: string;
}

interface LegalDocumentApi extends LegalSummaryApi {
  /** Temizlenmiş HTML; şirket bilgileri doldurulmuş. */
  content: string;
}

export interface LegalDocSummary {
  key: LegalKey;
  href: string;
  title: string;
  summary: string;
  updated: string;
}

export interface LegalDocContent {
  key: LegalKey;
  href: string;
  title: string;
  updated: string;
  html: string;
}

const keyByCmsKey = Object.fromEntries(Object.entries(legalCmsKey).map(([key, cmsKey]) => [cmsKey, key])) as Record<string, LegalKey>;

export interface LegalDocList {
  /** Sitede gösterilecek belgeler (CMS'te yayında olanlar), koddaki sırayla. */
  docs: LegalDocSummary[];
  updated: string;
  /** Yayında olmayan belgelerin adresleri; footer gibi statik bağlantı listelerinden düşülür. */
  hiddenHrefs: string[];
}

const staticLegalList: LegalDocList = {
  docs: legalDocs.map(({ key, href, title, summary, updated }) => ({ key, href, title, summary, updated })),
  updated: legalHub.updated as string,
  hiddenHrefs: [],
};

// Belge kümesi ve sırası koddaki adreslerden gelir; CMS her belgenin metnini ve yayın durumunu
// verir. CMS listesinde olmayan belge admin'de yayından kaldırılmıştır: sitede hiç görünmez.
const mapLegalList = (items: LegalSummaryApi[]): LegalDocList => {
  const byKey = new Map(items.map((i) => [keyByCmsKey[i.key], i]));
  const docs: LegalDocSummary[] = [];
  const hiddenHrefs: string[] = [];
  for (const d of staticLegalList.docs) {
    const cms = byKey.get(d.key);
    if (cms) docs.push({ ...d, title: cms.title, summary: cms.summary, updated: formatDate(cms.revisedOn) });
    else hiddenHrefs.push(d.href);
  }
  // Yasal merkezin "Son güncelleme" tarihi: yayındaki en yeni belgenin tarihi.
  const latest = items.reduce<string | null>((max, i) => (max === null || i.revisedOn > max ? i.revisedOn : max), null);
  return { docs, updated: latest ? formatDate(latest) : staticLegalList.updated, hiddenHrefs };
};

export function useLegalDocs() {
  return useCms('/v1/public/legal', staticLegalList, mapLegalList);
}

const mapLegalDoc = (d: LegalDocumentApi): LegalDocContent => {
  const key = keyByCmsKey[d.key];
  return { key, href: legalByKey[key].href, title: d.title, updated: formatDate(d.revisedOn), html: d.content };
};

const staticLegalDoc = (key: LegalKey): LegalDocContent => {
  const doc = legalByKey[key];
  return { key, href: doc.href, title: doc.title, updated: doc.updated, html: staticLegalHtml(doc) };
};

export function useLegalDoc(key: LegalKey): LegalDocContent {
  return useCms(`/v1/public/legal/${legalCmsKey[key]}`, staticLegalDoc(key), mapLegalDoc);
}

/* ---------- Fiyatlandırma ---------- */

export interface PricingData {
  pricing: Pricing;
  /** Hesaplayıcı veri değişince seçimleri sıfırlar (statik yedekten CMS'e geçişte). */
  source: 'static' | 'cms';
}

const staticPricing: PricingData = { pricing: pricingPage.pricing, source: 'static' };

// API yanıtı hesaplayıcının beklediği biçimde (lib/pricing.ts → Pricing); eşleme gerekmez.
const mapPricing = (pricing: Pricing): PricingData => ({ pricing, source: 'cms' });

export function usePricing(): PricingData {
  return useCms('/v1/public/pricing', staticPricing, mapPricing);
}

/* ---------- Kurucular ---------- */

interface FounderApi {
  slug: string;
  name: string;
  role: string;
  photoUrl: string | null;
  /** Ana sayfa kartı; temizlenmiş HTML. */
  summary: string;
  /** Kurucu sayfasındaki metnin tamamı; temizlenmiş HTML. */
  content: string;
}

export interface Founder {
  slug: string;
  name: string;
  role: string;
  /** Yoksa baş harfler gösterilir. */
  photoUrl: string | null;
  summaryHtml: string;
  contentHtml: string;
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Statik yedek, admin editörünün ürettiği HTML biçimine çevrilir (kalın paragraf = <p><strong>);
// CMS'ten gelen metinle aynı bileşenden ve aynı görünümle geçer. Backend'deki ilk aktarım
// (SeedFoundersAndIntegrations) da bu dönüşümle üretildi.
const paragraphsHtml = (paragraphs: readonly FounderParagraph[]) =>
  paragraphs.map((p) => (p.strong ? `<p><strong>${escapeHtml(p.text)}</strong></p>` : `<p>${escapeHtml(p.text)}</p>`)).join('');

const staticFounders: Founder[] = founders.people.map((p) => ({
  slug: p.slug,
  name: p.name,
  role: p.role,
  photoUrl: null,
  summaryHtml: paragraphsHtml('teaser' in p ? p.teaser : p.lead),
  contentHtml: paragraphsHtml([...p.lead, ...p.rest]),
}));

const mapFounders = (items: FounderApi[]): Founder[] =>
  items.map((f) => ({ slug: f.slug, name: f.name, role: f.role, photoUrl: f.photoUrl, summaryHtml: f.summary, contentHtml: f.content }));

/**
 * Yayındaki kurucular, sıralı. Admin'de yayından kaldırılan kurucu listede yoktur (sayfası 404).
 * `settled`: kurucu sayfası, admin'den yeni eklenmiş (statik yedekte olmayan) bir kurucu için
 * CMS yanıtı gelmeden "bulunamadı" göstermesin diye.
 */
export function useFounders(): { founders: Founder[]; settled: boolean } {
  const { data, settled: done } = useCmsState('/v1/public/founders', staticFounders, mapFounders);
  return { founders: data, settled: done };
}

/* ---------- Entegrasyonlar ---------- */

interface IntegrationApi {
  name: string;
  logoUrl: string | null;
}

interface IntegrationsApi {
  categories: { key: string; title: string; description: string; integrations: IntegrationApi[] }[];
  featured: IntegrationApi[];
}

export interface Brand {
  name: string;
  /** Tam adres (statik yedekte de); yoksa marka adı yazı olarak çizilir. */
  logoUrl: string | null;
}

export type BrandCategory = Omit<IntegrationCategory, 'brands'> & { brands: Brand[] };

export interface IntegrationsData {
  categories: BrandCategory[];
  /** Ana sayfadaki logo şeridi. */
  featured: Brand[];
}

const staticCategories = entegrasyonlarPage.systems.categories;

// Kategorinin rengi ve ikonu tasarımın parçası, kodda kalır; CMS başlığı, açıklamayı ve markaları
// verir. Admin'de kategori eklenemez ama bilinmeyen anahtar gelirse ilk kategorinin görünümü kullanılır.
const categoryLook = (key: string) => {
  const { color, rgb, icon } = staticCategories.find((c) => c.id === key) ?? staticCategories[0];
  return { color, rgb, icon };
};

const staticIntegrations: IntegrationsData = {
  categories: staticCategories.map((c) => ({
    ...c,
    brands: c.brands.map((b) => ({ name: b.name, logoUrl: b.logo ? asset(b.logo) : null })),
  })),
  featured: landing.strip.logos.map((l) => ({ name: l.name, logoUrl: l.src ? asset(l.src) : null })),
};

const mapIntegrations = (data: IntegrationsApi): IntegrationsData => ({
  categories: data.categories.map((c) => ({
    id: c.key,
    title: c.title,
    desc: c.description,
    ...categoryLook(c.key),
    brands: c.integrations,
  })),
  featured: data.featured,
});

export function useIntegrations(): IntegrationsData {
  return useCms('/v1/public/integrations', staticIntegrations, mapIntegrations);
}

/* ---------- Menü ---------- */

interface MenuLinkApi {
  label: string;
  description: string | null;
  icon: string | null;
  href: string;
  isSoon: boolean;
  /** Kısa durum rozeti (ör. "Beta"); eski backend'de alan yok, o yüzden isteğe bağlı. */
  badge?: string | null;
}

interface MenuApi {
  header: {
    label: string;
    href: string;
    panel: {
      columns: { title: string; links: MenuLinkApi[] }[];
      aside: { title: string; description: string | null; href: string; ctaLabel: string; isButton: boolean } | null;
    } | null;
  }[];
  footer: { title: string; links: { label: string; href: string }[] }[];
}

export interface SiteMenu {
  /** Üst menü (giriş ve demo düğmeleri kodda: `nav.login`, `nav.cta`). */
  items: NavItem[];
  footerColumns: FooterColumn[];
}

const staticMenu: SiteMenu = { items: nav.items, footerColumns: footer.columns };

// İkon adı sitenin ikon setinden gelir; sette olmayan ad (admin'de yanlış yazım) çizilmez.
const iconOf = (name: string | null): IconName | undefined => (name && name in icons ? (name as IconName) : undefined);

const mapMenu = (menu: MenuApi): SiteMenu => ({
  items: menu.header.map((item) => ({
    label: item.label,
    href: item.href,
    panel: item.panel
      ? {
          columns: item.panel.columns.map((c) => ({
            title: c.title,
            links: c.links.map((l) => ({
              label: l.label,
              desc: l.description ?? undefined,
              icon: iconOf(l.icon),
              href: l.href,
              soon: l.isSoon,
              badge: l.badge || undefined,
            })),
          })),
          aside: item.panel.aside
            ? {
                title: item.panel.aside.title,
                desc: item.panel.aside.description ?? undefined,
                href: item.panel.aside.href,
                cta: item.panel.aside.ctaLabel,
                button: item.panel.aside.isButton,
              }
            : undefined,
        }
      : undefined,
  })),
  footerColumns: menu.footer,
});

/**
 * Üst menü ve footer bağlantıları (admin → Menü). Bağlantılar yalnız sitede var olan sayfalara
 * verilebilir (backend doğrular); "Çok Yakında" sayfaları yine koddaki menüden (`menuPages`) türer.
 */
export function useMenu(): SiteMenu {
  return useCms('/v1/public/menu', staticMenu, mapMenu);
}
